import * as React from 'react';
import { atom, useGetRecoilValueInfo_UNSTABLE, useRecoilState, useSetRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import type { FilesDropZoneAccept } from '@gitbook/spine-react';

/** Filter files that can be listed. It can also accept an array of extension */
export type InsertFilesAccept = FilesDropZoneAccept;

export interface InsertFilesOptions {
    title?: string;
    /** Which files to accept */
    accept?: InsertFilesAccept;
    /** Should it accept external urls as well */
    acceptURL?: boolean;
}

export interface InsertFilesHook {
    /**
     * Prompt the user to select or upload files.
     */
    prompt: (
        options?: InsertFilesOptions
    ) => Promise<Array<api.ContentRefFile | api.ContentRefURL>>;

    /**
     * Test if the dialog is currently open.
     */
    isOpen: () => boolean;
}

export type InsertFilesState = {
    options: InsertFilesOptions;
    onComplete: (files: Array<api.ContentRefFile | api.ContentRefURL>) => void;
};

/**
 * State for the insert files dialog in the current opened space.
 * As a global state so that it can easily be used by the editor.
 */
const insertFilesAtom = atom<null | InsertFilesState>({
    key: 'insertFilesAtom',
    default: null,
});

/**
 * Get the current state of the isnert files dialog.
 */
export function useInsertFilesState() {
    return useRecoilState(insertFilesAtom);
}

/**
 * Open the files manager and prompt to select a file.
 * A different contentId and branchId can be provided to the manager.
 */
export function useInsertFiles(): InsertFilesHook {
    const setInsertFiles = useSetRecoilState(insertFilesAtom);
    const getRecoilValueInfo = useGetRecoilValueInfo_UNSTABLE();
    // biome-ignore lint/correctness/useExhaustiveDependencies: getRecoilValueInfo is not stable and cannot be used a dep
    return React.useMemo<InsertFilesHook>(() => {
        return {
            prompt: (options) => {
                return new Promise((resolve) => {
                    setInsertFiles({
                        options: options ?? {},
                        onComplete: resolve,
                    });
                });
            },
            isOpen: () => {
                // isModified will be false when default value (null) and true when
                // filesManagerAtom has been modified, i.e. is open
                const { isModified } = getRecoilValueInfo(insertFilesAtom);
                return isModified;
            },
        };
    }, [setInsertFiles]);
}
